import { Info } from "shared/Schemas";

// アプリケーションバージョン
export const appVersion = "1.1.0";

// フロアマップの画像型
export type FloorImageType = {
  src: string; // S3パス(例: "customerId/floorImages/1F.png")
  name: string; // 表示名(例: "1F", "2F")
};

// カスタム画像型
export type CustomImageType = {
  key: string;
  src: string;
};

export const cameraSettings = {
  minDistance: 10,
  maxDistance: 140,
  defaultThreshold: 150,
};

export const t2SEngines = {
  voicevox: "VOICEVOX",
  openai: "OpenAI",
  aivisSpeech: "AivisSpeech",
};

// 会話モードの状態値
export const talkModes = {
  stopped: 0, // 停止状態
  standby: 1, // 待機状態
  active: 2, // アクティブ状態
};

// スタイルモードの状態値
export const styleModes = {
  casual: "casual",
  formal: "formal",
};

// フィードバックモードの状態値
export const feedbackModes = {
  simple: 0, // good/bad
  detail: 1, // 1-5の星評価
};

// 設置場所
export const currentLocale = "ja-JP";

// 音声合成エンジンの情報を取得する関数
export const getNameAndStyle = (
  engine: string,
  info: Info | null
): { name: string; style: string } => {
  if (!info) return { name: "", style: "" };

  switch (engine) {
    case "OpenAI":
      return { name: info.styleName ?? "", style: info.name ?? "" };
    case "VOICEVOX":
    case "AivisSpeech":
      return { name: info.name ?? "", style: info.styleName ?? "" };
    default:
      return { name: "", style: "" };
  }
};

export const googlemapCategories = [
  { label: "会計士", value: "accounting" },
  { label: "空港", value: "airport" },
  { label: "遊園地", value: "amusement_park" },
  { label: "水族館", value: "aquarium" },
  { label: "アートギャラリー", value: "art_gallery" },
  { label: "ATM", value: "atm" },
  { label: "パン屋", value: "bakery" },
  { label: "銀行", value: "bank" },
  { label: "バー", value: "bar" },
  { label: "美容院", value: "beauty_salon" },
  { label: "自転車屋", value: "bicycle_store" },
  { label: "本屋", value: "book_store" },
  { label: "ボウリング場", value: "bowling_alley" },
  { label: "バス停", value: "bus_station" },
  { label: "カフェ", value: "cafe" },
  { label: "キャンプ場", value: "campground" },
  { label: "車販売店", value: "car_dealer" },
  { label: "レンタカー", value: "car_rental" },
  { label: "自動車修理", value: "car_repair" },
  { label: "洗車場", value: "car_wash" },
  { label: "カジノ", value: "casino" },
  { label: "墓地", value: "cemetery" },
  { label: "教会", value: "church" },
  { label: "市役所", value: "city_hall" },
  { label: "服屋", value: "clothing_store" },
  { label: "コンビニ", value: "convenience_store" },
  { label: "裁判所", value: "courthouse" },
  { label: "歯医者", value: "dentist" },
  { label: "デパート", value: "department_store" },
  { label: "医者", value: "doctor" },
  { label: "薬局", value: "drugstore" },
  { label: "電気技師", value: "electrician" },
  { label: "家電量販店", value: "electronics_store" },
  { label: "大使館", value: "embassy" },
  { label: "消防署", value: "fire_station" },
  { label: "花屋", value: "florist" },
  { label: "葬儀場", value: "funeral_home" },
  { label: "家具屋", value: "furniture_store" },
  { label: "ガソリンスタンド", value: "gas_station" },
  { label: "ジム", value: "gym" },
  { label: "ヘアケア", value: "hair_care" },
  { label: "金物店", value: "hardware_store" },
  { label: "ヒンドゥー教寺院", value: "hindu_temple" },
  { label: "ホーム用品店", value: "home_goods_store" },
  { label: "病院", value: "hospital" },
  { label: "保険会社", value: "insurance_agency" },
  { label: "宝石店", value: "jewelry_store" },
  { label: "ランドリー", value: "laundry" },
  { label: "弁護士", value: "lawyer" },
  { label: "図書館", value: "library" },
  { label: "ライトレール駅", value: "light_rail_station" },
  { label: "酒屋", value: "liquor_store" },
  { label: "役所", value: "local_government_office" },
  { label: "鍵屋", value: "locksmith" },
  { label: "宿泊施設", value: "lodging" },
  { label: "宅配", value: "meal_delivery" },
  { label: "持ち帰り", value: "meal_takeaway" },
  { label: "モスク", value: "mosque" },
  { label: "映画レンタル", value: "movie_rental" },
  { label: "映画館", value: "movie_theater" },
  { label: "引越し会社", value: "moving_company" },
  { label: "美術館", value: "museum" },
  { label: "ナイトクラブ", value: "night_club" },
  { label: "塗装業者", value: "painter" },
  { label: "公園", value: "park" },
  { label: "駐車場", value: "parking" },
  { label: "ペットショップ", value: "pet_store" },
  { label: "調剤薬局", value: "pharmacy" },
  { label: "理学療法士", value: "physiotherapist" },
  { label: "配管工", value: "plumber" },
  { label: "警察署", value: "police" },
  { label: "郵便局", value: "post_office" },
  { label: "小学校", value: "primary_school" },
  { label: "不動産会社", value: "real_estate_agency" },
  { label: "飲食店", value: "restaurant" },
  { label: "屋根業者", value: "roofing_contractor" },
  { label: "RVパーク", value: "rv_park" },
  { label: "学校", value: "school" },
  { label: "中学校・高校", value: "secondary_school" },
  { label: "靴屋", value: "shoe_store" },
  { label: "ショッピングモール", value: "shopping_mall" },
  { label: "スパ", value: "spa" },
  { label: "スタジアム", value: "stadium" },
  { label: "倉庫", value: "storage" },
  { label: "店舗", value: "store" },
  { label: "地下鉄駅", value: "subway_station" },
  { label: "スーパー", value: "supermarket" },
  { label: "シナゴーグ（ユダヤ教会堂）", value: "synagogue" },
  { label: "タクシー乗り場", value: "taxi_stand" },
  { label: "観光地", value: "tourist_attraction" },
  { label: "電車の駅", value: "train_station" },
  { label: "公共交通の駅", value: "transit_station" },
  { label: "旅行代理店", value: "travel_agency" },
  { label: "大学", value: "university" },
  { label: "動物病院", value: "veterinary_care" },
  { label: "動物園", value: "zoo" },
];

// レスポンス内の特殊タグ
export const responseTags = {
  imgTag: "<img", // 画像タグの開始文字列
  messageEnd: "message_end/", // メッセージ終了を示すタグ
};

export const defaultPrompt = `### 条件1：時間表記の変換
営業時間などに「:」が含まれている場合、必ず「時」「分」の形式に変換すること
- 変換前例：営業時間は12:00-17:30までです。
- 変換後例：営業時間は12時-17時30分までです。

### 条件2：二次元コードの使用
- 回答には URLの文字列やリンクは表示しないこと
URL(website)がある場合は二次元コード（<img>タグ）に変換し、回答内に埋め込むこと
URL(website)がなければ表示しないこと

### 条件3：出力内容が複数ある場合
- 回答内容が複数項目にわたる場合は、**最初の1項目のみ**を出力すること

### 条件4：回答内容が曖昧な場合
質問者に断りを入れて、もう一度質問してほしい旨を伝えること

### 条件5：周辺情報
周辺の情報について聞かれた場合は、最後に「周辺情報についてはサイドメニューのマップもご利用ください」と勧めること

### 条件6：二次元コード
二次元コード(imgタグ)は必ず最後に出力し、二次元コード(imgタグ)の後は文章を出力しないこと
二次元コードは「https://api.qrserver.com」で生成した物を利用すること`;

export const warmupPrompt = `
これはシステム連携用のリクエストです。
出力はASCII文字列で「WARMED」のみとしてください。
他の文字列や説明文、句読点、改行などは一切含めないでください。
`;
